import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Edit, Save } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useOrders } from '@/hooks/useOrders';
import { useVehicleTypes } from '@/hooks/useVehicleTypes';
import { ScrollArea } from '@/components/ui/scroll-area';

const orderSchema = z.object({
  pickup_street_number: z.string().trim().max(10).optional(),
  pickup_street_name: z.string().trim().min(3).max(200),
  pickup_postal_code: z.string().trim().min(4).max(10),
  pickup_city: z.string().trim().min(2).max(100),
  pickup_country: z.string().trim().min(2).max(100),
  pickup_date: z.date({ required_error: 'Date requise' }),
  pickup_contact: z.string().trim().min(2).max(100),
  pickup_phone: z.string().trim().min(8).max(20),
  start_details: z.string().trim().max(1000).optional(),
  delivery_street_number: z.string().trim().max(10).optional(),
  delivery_street_name: z.string().trim().min(3).max(200),
  delivery_postal_code: z.string().trim().min(4).max(10),
  delivery_city: z.string().trim().min(2).max(100),
  delivery_country: z.string().trim().min(2).max(100),
  delivery_date: z.date({ required_error: 'Date requise' }),
  delivery_contact: z.string().trim().min(2).max(100),
  delivery_phone: z.string().trim().min(8).max(20),
  delivery_details: z.string().trim().max(1000).optional(),
  cargo_title: z.string().trim().min(3).max(200),
  trailer_type: z.string().min(1),
  weight_kg: z.coerce.number().positive().max(100000),
  volume_m3: z.coerce.number().positive().max(200).optional().or(z.literal('')),
  pallets_count: z.coerce.number().int().positive().max(100).optional().or(z.literal('')),
  cargo_description: z.string().trim().min(10).max(1000),
  special_instructions: z.string().trim().max(1000).optional(),
  proposed_price: z.coerce.number().positive().max(1000000).optional().or(z.literal('')),
  marketplace_visible: z.boolean(),
});

type OrderFormData = z.infer<typeof orderSchema>;


interface EditOrderDialogProps {
  order: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditOrderDialog = ({ order, open, onOpenChange }: EditOrderDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updateOrder } = useOrders();
  const { vehicleTypes } = useVehicleTypes();

  const form = useForm<OrderFormData>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      pickup_street_number: order.pickup_street_number || '',
      pickup_street_name: order.pickup_street_name || '',
      pickup_postal_code: order.pickup_postal_code || '',
      pickup_city: order.pickup_city || '',
      pickup_country: order.pickup_country || 'France',
      pickup_date: order.pickup_date ? new Date(order.pickup_date) : new Date(),
      pickup_contact: order.pickup_contact || '',
      pickup_phone: order.pickup_phone || '',
      start_details: order.start_details || '',
      delivery_street_number: order.delivery_street_number || '',
      delivery_street_name: order.delivery_street_name || '',
      delivery_postal_code: order.delivery_postal_code || '',
      delivery_city: order.delivery_city || '',
      delivery_country: order.delivery_country || 'France',
      delivery_date: order.delivery_date ? new Date(order.delivery_date) : new Date(),
      delivery_contact: order.delivery_contact || '',
      delivery_phone: order.delivery_phone || '',
      delivery_details: order.delivery_details || '',
      cargo_title: order.cargo_title || '',
      trailer_type: order.trailer_type || '',
      weight_kg: order.weight_kg || 0,
      volume_m3: order.volume_m3 || '',
      pallets_count: order.pallets_count || '',
      cargo_description: order.cargo_description || '',
      special_instructions: order.special_instructions || '',
      proposed_price: order.proposed_price || '',
      marketplace_visible: order.marketplace_visible || false,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        pickup_street_number: order.pickup_street_number || '',
        pickup_street_name: order.pickup_street_name || '',
        pickup_postal_code: order.pickup_postal_code || '',
        pickup_city: order.pickup_city || '',
        pickup_country: order.pickup_country || 'France',
        pickup_date: order.pickup_date ? new Date(order.pickup_date) : new Date(),
        pickup_contact: order.pickup_contact || '',
        pickup_phone: order.pickup_phone || '',
        start_details: order.start_details || '',
        delivery_street_number: order.delivery_street_number || '',
        delivery_street_name: order.delivery_street_name || '',
        delivery_postal_code: order.delivery_postal_code || '',
        delivery_city: order.delivery_city || '',
        delivery_country: order.delivery_country || 'France',
        delivery_date: order.delivery_date ? new Date(order.delivery_date) : new Date(),
        delivery_contact: order.delivery_contact || '',
        delivery_phone: order.delivery_phone || '',
        delivery_details: order.delivery_details || '',
        cargo_title: order.cargo_title || '',
        trailer_type: order.trailer_type || '',
        weight_kg: order.weight_kg || 0,
        volume_m3: order.volume_m3 || '',
        pallets_count: order.pallets_count || '',
        cargo_description: order.cargo_description || '',
        special_instructions: order.special_instructions || '',
        proposed_price: order.proposed_price || '',
        marketplace_visible: order.marketplace_visible || false,
      });
    }
  }, [open, order, form]);

  const onSubmit = async (data: OrderFormData) => {
    setIsSubmitting(true);
    try {
      const pickupAddress = [
        data.pickup_street_number,
        data.pickup_street_name,
        data.pickup_postal_code,
        data.pickup_city,
        data.pickup_country
      ].filter(Boolean).join(', ');

      const deliveryAddress = [
        data.delivery_street_number,
        data.delivery_street_name,
        data.delivery_postal_code,
        data.delivery_city,
        data.delivery_country
      ].filter(Boolean).join(', ');

      await updateOrder({
        id: order.id,
        pickup_address: pickupAddress,
        pickup_street_number: data.pickup_street_number,
        pickup_street_name: data.pickup_street_name,
        pickup_postal_code: data.pickup_postal_code,
        pickup_city: data.pickup_city,
        pickup_country: data.pickup_country,
        pickup_date: data.pickup_date.toISOString(),
        pickup_contact: data.pickup_contact,
        pickup_phone: data.pickup_phone,
        start_details: data.start_details,
        delivery_address: deliveryAddress,
        delivery_street_number: data.delivery_street_number,
        delivery_street_name: data.delivery_street_name,
        delivery_postal_code: data.delivery_postal_code,
        delivery_city: data.delivery_city,
        delivery_country: data.delivery_country,
        delivery_date: data.delivery_date.toISOString(),
        delivery_contact: data.delivery_contact,
        delivery_phone: data.delivery_phone,
        delivery_details: data.delivery_details,
        cargo_title: data.cargo_title,
        trailer_type: data.trailer_type,
        weight_kg: data.weight_kg,
        volume_m3: data.volume_m3 ? Number(data.volume_m3) : undefined,
        pallets_count: data.pallets_count ? Number(data.pallets_count) : undefined,
        cargo_description: data.cargo_description,
        special_instructions: data.special_instructions,
        proposed_price: data.proposed_price ? Number(data.proposed_price) : undefined,
        marketplace_visible: data.marketplace_visible,
      });

      toast.success('Commande modifiée avec succès');
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la modification');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-medium text-[#37699E] font-['Lexend_Deca']">
            Modifier la commande {order.order_number}
          </DialogTitle>
          <DialogDescription>
            Modifiez les informations de votre transport
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(90vh-140px)] pr-4">
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Départ */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-[#37699E] uppercase tracking-wide">Départ</h3>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Numéro</Label>
                  <Input {...form.register('pickup_street_number')} placeholder="123" />
                </div>
                <div className="col-span-3 space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Nom de rue *</Label>
                  <Input {...form.register('pickup_street_name')} placeholder="Rue de la République" />
                  {form.formState.errors.pickup_street_name && (
                    <p className="text-xs text-[#FF7171]">{form.formState.errors.pickup_street_name.message}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Code postal *</Label>
                  <Input {...form.register('pickup_postal_code')} placeholder="75000" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Ville *</Label>
                  <Input {...form.register('pickup_city')} placeholder="Paris" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Pays *</Label>
                  <Input {...form.register('pickup_country')} placeholder="France" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Date de départ *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left", !form.watch('pickup_date') && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.watch('pickup_date') ? format(form.watch('pickup_date'), 'PPP', { locale: fr }) : 'Sélectionner'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={form.watch('pickup_date')} onSelect={(date) => date && form.setValue('pickup_date', date)} locale={fr} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Contact départ *</Label>
                  <Input {...form.register('pickup_contact')} placeholder="Nom du contact" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Téléphone *</Label>
                  <Input {...form.register('pickup_phone')} placeholder="0612345678" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Détails (optionnel)</Label>
                  <Textarea {...form.register('start_details')} placeholder="Informations complémentaires" rows={2} />
                </div>
              </div>
            </div>

            {/* Livraison */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-[#37699E] uppercase tracking-wide">Livraison</h3>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Numéro</Label>
                  <Input {...form.register('delivery_street_number')} placeholder="456" />
                </div>
                <div className="col-span-3 space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Nom de rue *</Label>
                  <Input {...form.register('delivery_street_name')} placeholder="Avenue des Champs" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Code postal *</Label>
                  <Input {...form.register('delivery_postal_code')} placeholder="75000" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Ville *</Label>
                  <Input {...form.register('delivery_city')} placeholder="Paris" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Pays *</Label>
                  <Input {...form.register('delivery_country')} placeholder="France" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Date de livraison *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left", !form.watch('delivery_date') && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.watch('delivery_date') ? format(form.watch('delivery_date'), 'PPP', { locale: fr }) : 'Sélectionner'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={form.watch('delivery_date')} onSelect={(date) => date && form.setValue('delivery_date', date)} locale={fr} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Contact livraison *</Label>
                  <Input {...form.register('delivery_contact')} placeholder="Nom du contact" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Téléphone *</Label>
                  <Input {...form.register('delivery_phone')} placeholder="0612345678" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Détails (optionnel)</Label>
                  <Textarea {...form.register('delivery_details')} placeholder="Informations complémentaires" rows={2} />
                </div>
              </div>
            </div>

            {/* Marchandise */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-[#37699E] uppercase tracking-wide">Marchandise</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Titre de la marchandise *</Label>
                  <Input {...form.register('cargo_title')} placeholder="Palettes de marchandises" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Type de véhicule *</Label>
                  <Select value={form.watch('trailer_type')} onValueChange={(value) => form.setValue('trailer_type', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes?.map((type) => (
                        <SelectItem key={type.id} value={type.trailer_type}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Poids (kg) *</Label>
                  <Input type="number" {...form.register('weight_kg')} placeholder="1000" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Volume (m³)</Label>
                  <Input type="number" step="0.01" {...form.register('volume_m3')} placeholder="10.5" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Palettes</Label>
                  <Input type="number" {...form.register('pallets_count')} placeholder="5" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Description *</Label>
                <Textarea {...form.register('cargo_description')} placeholder="Description détaillée" rows={3} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Instructions spéciales</Label>
                <Textarea {...form.register('special_instructions')} placeholder="Instructions particulières" rows={2} />
              </div>
            </div>

            {/* Tarification */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-[#37699E] uppercase tracking-wide">Tarification</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-[#5A6B7A] uppercase">Prix proposé (€)</Label>
                  <Input type="number" step="0.01" {...form.register('proposed_price')} placeholder="850" />
                </div>
                <div className="space-y-2 flex items-center gap-3 pt-6">
                  <Switch checked={form.watch('marketplace_visible')} onCheckedChange={(checked) => form.setValue('marketplace_visible', checked)} />
                  <Label className="text-xs font-medium text-[#2D3748]">Visible sur le marketplace</Label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1 bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold">
                <Save className="mr-2 h-4 w-4" />
                {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
